/* global instantsearch, algoliasearch */
/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @return {string} Truncated text with ellipsis if needed
 */
const truncateText = ( text, maxLength ) => {

	const str = String( text || '' );
	if ( str.length <= maxLength ) {

		return str;

	}
	return `${str.substring( 0, maxLength - 3 )}...`;

};

// Add constant for minimum characters
const MIN_CHARS_TO_SEARCH = 3;

// List of blocked search terms
const BLOCKED_SEARCH_TERMS = ['xxx', 'porn', 'sex', 'adult', 'nude', 'xnxx', 'xvideos', 'redtube', 'brazzers'];

/**
 * Check if a query contains blocked terms
 * @param {string} query - Search query to check
 * @return {boolean} True if query contains blocked terms
 */
function containsBlockedTerm( query ) {

	if ( !query || typeof query !== 'string' ) {

		return false;

	}

	const normalizedQuery = query.toLowerCase().trim();
	return BLOCKED_SEARCH_TERMS.some( term => normalizedQuery.includes( term.toLowerCase() ) );

}

/**
 * Group and limit hits by post type.
 * @param {Array} hits - Search hits to group.
 * @param {number} limit - Maximum number of hits per group.
 * @return {Object} Grouped hits.
 */
function groupAndLimitHits( hits, limit = 5 ) {

	const groups = hits.reduce( ( acc, hit ) => {

		const postType = hit.post_type || 'other';
		const formattedPostType = postType
			.split( '_' )
			.map( word => word.charAt( 0 ).toUpperCase() + word.slice( 1 ) )
			.join( ' ' );

		if ( !acc[formattedPostType] ) {

			acc[formattedPostType] = [];

		}

		if ( acc[formattedPostType].length < limit ) {

			acc[formattedPostType].push( hit );

		}

		return acc;

	}, {} );

	return groups;

}

/**
 * Move the pinned record to the top of the hits array if present.
 * @param {Array} hits - The array of Algolia hits.
 * @param {Array} pinnedPostIds - Array of pinned post IDs
 * @return {Array} The hits array with the pinned record at the top (if present).
 */
function pinRecordToTop( hits, pinnedPostIds ) {

	const pinnedIndex = hits.findIndex( hit => pinnedPostIds.includes( hit.objectID ) );
	if ( pinnedIndex > -1 ) {

		const [pinned] = hits.splice( pinnedIndex, 1 );
		hits.unshift( pinned );

	}
	return hits;

}

/**
 * Fetch a pinned record from Algolia by objectID using the global algoliasearch client (from CDN).
 * @param {string} appId - Algolia App ID
 * @param {string} searchApiKey - Algolia Search-Only API Key
 * @param {string} indexName - Algolia Index Name
 * @param {Array} pinnedPostIds - Array of pinned post IDs
 * @return {Promise<Object|null>} - Resolves to the record or null if not found
 */
function fetchPinnedRecord( appId, searchApiKey, indexName, pinnedPostIds ) {

	const algoliaClient = algoliasearch( appId, searchApiKey );
	const index = algoliaClient.initIndex( indexName );

	// Handle multiple pinned posts
	if ( Array.isArray( pinnedPostIds ) && pinnedPostIds.length > 1 ) {

		return index.getObjects( pinnedPostIds )
			.then( records => records.results )
			.catch( () => [] );

	}

	// Handle single pinned post
	if ( pinnedPostIds && pinnedPostIds.length === 1 ) {

		return index.getObject( pinnedPostIds[0] )
			.then( record => record )
			.catch( () => null );

	}

	return Promise.resolve( null );

}

/**
 * Render grouped hits in the autocomplete results container.
 * @param {Array} hits - The array of Algolia hits to render.
 * @param {Array} pinnedPostIds - Array of pinned post IDs
 */
function renderWithHits( hits, pinnedPostIds ) {

	const resultsContainer = document.querySelector( '.opt-search__results' );
	let html = '';

	// Extract pinned posts
	const pinned = hits.filter( hit => pinnedPostIds.includes( hit.objectID ) );
	const rest = hits.filter( hit => !pinnedPostIds.includes( hit.objectID ) );

	if ( pinned.length ) {

		html += `
			<div class="opt-search__result-group" role="group" aria-label="Pinned">
				<h3 class="opt-search__group-title">Pinned</h3>
				<ul class="opt-search__results-list">
					${pinned.map( hit => `
						<li class="opt-search__result-item">
							<a href="${hit.permalink || '#'}"
							   class="opt-search__result-link"
							   role="option">
								${hit.title || 'Untitled'}
							</a>
						</li>
					` ).join( '' )}
				</ul>
			</div>
		`;

	}

	// Now group and render the rest
	const groupedHits = groupAndLimitHits( rest, 5 );
	html += Object.entries( groupedHits )
		.map( ( [postType, groupHits] ) => `
			<div class="opt-search__result-group" role="group" aria-label="${postType} results">
				<h3 class="opt-search__group-title">${postType}</h3>
				<ul class="opt-search__results-list">
					${groupHits.map( hit => `
						<li class="opt-search__result-item">
							<a href="${hit.permalink}"
							   class="opt-search__result-link"
							   role="option">
								${instantsearch.highlight( { attribute: 'title', hit } )}
							</a>
						</li>
					` ).join( '' )}
				</ul>
			</div>
		` ).join( '' );

	resultsContainer.innerHTML = html;
	resultsContainer.style.display = 'block';

}

/**
 * Initialize Algolia autocomplete search
 * @return {void}
 */
export default function initSearch() {

	const { appId, searchApiKey, indexName, pinnedPostIds } = window.algoliaConfig;

	// Ensure algoliaConfig is available
	if ( !window.algoliaConfig ) {

		// eslint-disable-next-line no-console
		console.error( 'Algolia configuration is not available' );
		return;

	}

	// Validate required configuration
	if ( !appId || !searchApiKey || !indexName ) {

		// eslint-disable-next-line no-console
		console.error( 'Missing required Algolia configuration' );
		return;

	}

	// Initialize Algolia client with WordPress-provided credentials
	const algoliaClient = algoliasearch( appId, searchApiKey );


	// Create a custom client that handles empty queries more intelligently
	const searchClient = {
		...algoliaClient,
		/**
		 * Handle search requests
		 * @param {Array} requests - Search requests
		 * @return {Promise} Search results
		 */
		search( requests ) {

			// Check if any request contains blocked terms
			const hasBlockedTerm = requests.some( ( { params } ) => {

				const query = params.query || '';
				return containsBlockedTerm( query );

			} );

			// Block search if query contains blocked terms
			if ( hasBlockedTerm ) {

				return Promise.resolve( {
					results: requests.map( () => ( {
						hits: [],
						nbHits: 0,
						nbPages: 0,
						page: 0,
						processingTimeMS: 0,
						exhaustiveNbHits: true,
						query: '',
						params: ''
					} ) ),
				} );

			}

			const shouldSearch = requests.some( ( { params } ) => {

				const query = params.query || '';
				const hasQuery = query.length >= MIN_CHARS_TO_SEARCH;
				const hasRefinements = Boolean(
					params.facetFilters ||
					params.filters ||
					params.insideBoundingBox ||
					params.insidePolygon ||
					params.aroundLatLng ||
					params.aroundRadius
				);
				return hasQuery || hasRefinements;

			} );

			if ( !shouldSearch ) {

				return Promise.resolve( {
					results: requests.map( () => ( {
						hits: [],
						nbHits: 0,
						nbPages: 0,
						page: 0,
						processingTimeMS: 0,
						exhaustiveNbHits: true,
						query: '',
						params: ''
					} ) ),
				} );

			}

			return algoliaClient.search( requests );

		},
	};

	const search = instantsearch( {
		searchClient,
		indexName: indexName,
		routing: false,
		initialUiState: {
			[indexName]: {
				query: new URLSearchParams( window.location.search ).get( 's' ) || '',
				page: parseInt( new URLSearchParams( window.location.search ).get( 'page' ) ) || 1
			}
		},

		/**
		 * Search function
		 * @param {*} helper - Helper object
		 */
		searchFunction( helper ) {

			// Block search if query contains blocked terms
			const query = helper.state.query || '';
			if ( containsBlockedTerm( query ) ) {

				return;

			}

			// Only search if query meets minimum length or has refinements
			if ( query.length >= MIN_CHARS_TO_SEARCH || helper.hasRefinements() ) {

				helper.search();

			}

		}
	} );

	// Add advanced configuration for better search results
	search.addWidgets( [
		instantsearch.widgets.configure( {
			// Improve search quality
			ignorePlurals: true,
			removeStopWords: true,
			// Handle no results scenarios
			removeWordsIfNoResults: 'allOptional',
			// Set minimum character length for typo tolerance
			minWordSizefor1Typo: 4,
			minWordSizefor2Typos: 8,
			// Adjust ranking criteria
			distinct: true,
			attributesToSnippet: ['content:50'],
			snippetEllipsisText: '...',
			hitsPerPage: 15
		} )
	] );

	if( document.querySelector( '.js-searchBar' ) ) {

		/**
		 * Update search phrase in the UI
		 * @param {string} query - Search query
		 */
		const updateSearchPhrase = ( query ) => {

			const searchPhraseElement = document.querySelector( '.search-phrase span' );
			if ( searchPhraseElement ) {

				searchPhraseElement.textContent = query || 'No search term';

			}

		};

		/**
		 * Render autocomplete
		 * @param {Object} renderOptions - Render options
		 * @param {boolean} isFirstRender - Whether this is the first render
		 * @return {void}
		 */
		const renderAutocomplete = ( renderOptions, isFirstRender ) => {

			const { indices, currentRefinement, refine, widgetParams } = renderOptions;

			/**
			 * Handle key down event
			 * @param {Object} event - Key down event
			 * @return {void}
			 */
			const handleKeyDown = ( event ) => {

				if ( event.key === 'Enter' && event.target.ownerDocument.activeElement === event.target ) {

					event.preventDefault();
					const currentQuery = event.target.value.trim();
					const isBlocked = containsBlockedTerm( currentQuery );

					// Don't redirect if query contains blocked terms
					if ( isBlocked ) {

						return;

					}

					const hasResults = indices && indices.length > 0 && indices[0].hits && indices[0].hits.length > 0;

					// Only proceed if there's a non-empty query AND there are results
					if ( hasResults && currentQuery ) {

						window.location.href = `${window.location.origin}/?s=${encodeURIComponent( currentQuery )}`;

					}

				}

			};

			if ( isFirstRender ) {

				const container = document.querySelector( '.js-searchInputWrapper' );

				// Create search icon
				const searchSvgIcon = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0156 13.021L16.1406 16.146" stroke="#99A7BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.11198 14.0626C11.8446 14.0626 14.0599 11.8473 14.0599 9.11467C14.0599 6.38201 11.8446 4.16675 9.11198 4.16675C6.37932 4.16675 4.16406 6.38201 4.16406 9.11467C4.16406 11.8473 6.37932 14.0626 9.11198 14.0626Z" stroke="#99A7BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

				// Create input
				const input = document.createElement( 'input' );
				input.classList.add( 'opt-search__input' );
				input.setAttribute( 'data-last-search', '' );
				input.setAttribute( 'placeholder', 'Search...' );
				input.setAttribute( 'autocomplete', 'off' );
				input.setAttribute( 'autocorrect', 'off' );
				input.setAttribute( 'spellcheck', 'false' );
				input.setAttribute( 'maxlength', '512' );
				input.setAttribute( 'type', 'search' );
				input.setAttribute( 'role', 'searchbox' );
				input.setAttribute( 'aria-label', 'Search' );
				input.setAttribute( 'id', 'searchInput' );

				// Create reset button
				const button = document.createElement( 'button' );
				button.classList.add( 'opt-search__reset' );
				button.setAttribute( 'type', 'button' );
				button.setAttribute( 'aria-label', 'Clear search' );
				// Set initial display state based on currentRefinement
				button.style.display = currentRefinement.trim() ? 'flex' : 'none';
				button.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M5 5L15 15" stroke="#99A7BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M15 5L5.00003 15" stroke="#99A7BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>`;

				// Create results container
				const resultsContainer = document.createElement( 'div' );
				resultsContainer.classList.add( 'opt-search__results' );
				resultsContainer.setAttribute( 'role', 'listbox' );

				let debounceTimer = null;
				let lastSearchTimestamp = 0;
				let lastInputValue = '';

				input.addEventListener( 'input', event => {

					const value = event.target.value;
					const trimmedValue = value.trim();
					const isBlocked = containsBlockedTerm( trimmedValue );

					// Clear previous timer
					clearTimeout( debounceTimer );

					// Update UI immediately
					input.setAttribute( 'data-last-search', value );
					button.style.display = trimmedValue ? 'flex' : 'none';

					// Check if we're on the search results page
					const isSearchPage = document.querySelector( '.js-refinment-list' );

					// Show blocked message if query contains blocked terms
					if ( isBlocked && trimmedValue ) {

						showEmptyState( resultsContainer, trimmedValue, true );
						return;

					}

					// Show empty state while typing
					if ( !trimmedValue && !isSearchPage ) {

						showEmptyState( resultsContainer );
						return;

					}

					// Update URL on search results page
					if ( isSearchPage ) {

						const currentUrl = new URL( window.location.href );
						if ( trimmedValue && !isBlocked ) {

							currentUrl.searchParams.set( 's', encodeURIComponent( value ) );

						} else {

							currentUrl.searchParams.delete( 's' );

						}
						window.history.replaceState( {}, '', currentUrl.toString() );

					}

					// Handle search based on minimum length (but not for blocked terms)
					if ( !isBlocked && ( trimmedValue.length >= MIN_CHARS_TO_SEARCH || isSearchPage ) ) {

						// Capture the current value to use in the timeout
						const capturedValue = value;

						debounceTimer = setTimeout( () => {

							refine( capturedValue );

						}, 250 );

					} else if ( isBlocked && trimmedValue ) {

						// Show blocked message immediately
						showEmptyState( resultsContainer, trimmedValue, true );

					} else {

						showEmptyState( resultsContainer );

					}

				} );

				// Update reset button handler
				button.addEventListener( 'click', () => {

					input.value = '';
					input.setAttribute( 'data-last-search', '' );
					button.style.display = 'none';
					input.focus();

					// Check if we're on the search results page
					const isSearchPage = document.querySelector( '.js-refinment-list' );

					if ( ! isSearchPage ) {

						// Just show empty state for autocomplete
						showEmptyState( resultsContainer );

					}

				} );

				// Create search icon container
				const searchSvg = document.createElement( 'div' );
				searchSvg.classList.add( 'opt-search__search-icon' );
				searchSvg.setAttribute( 'aria-hidden', 'true' );
				searchSvg.innerHTML = searchSvgIcon;

				// Append elements
				container.appendChild( searchSvg );
				container.appendChild( input );
				container.appendChild( button );
				if ( !document.querySelector( '.js-refinment-list' ) ) {

					container.appendChild( resultsContainer );

				}

				// Prevent form submission if input is within a form
				const form = input.closest( 'form' );
				if ( form ) {

					form.addEventListener( 'submit', e => e.preventDefault() );

				}

			}

			// Only update the input value if it's significantly different and not focused
			const searchInput = document.querySelector( widgetParams.container ).querySelector( 'input' );
			if ( searchInput &&
				searchInput.value !== currentRefinement &&
				searchInput.value.trim() !== currentRefinement.trim() &&
				document.activeElement !== searchInput ) {

				searchInput.value = currentRefinement;

			}

			// Clean up old event listener and add new one
			searchInput.removeEventListener( 'keydown', searchInput.enterKeyHandler );
			searchInput.enterKeyHandler = handleKeyDown;
			searchInput.addEventListener( 'keydown', searchInput.enterKeyHandler );

			// Update results container
			const resultsContainer = document.querySelector( '.opt-search__results' );
			if ( resultsContainer && !document.querySelector( '.js-refinment-list' ) ) {

				const hasResults = Boolean( indices?.[0]?.hits?.length );
				const hasQuery = Boolean( currentRefinement.trim() );
				const isBlocked = containsBlockedTerm( currentRefinement );

				// Show blocked message if query contains blocked terms
				if ( isBlocked && hasQuery ) {

					showEmptyState( resultsContainer, currentRefinement, true );
					return;

				}

				if ( hasResults ) {

					// Pin the record to the top if present in autocomplete
					let pinnedHits = pinRecordToTop( [ ...indices[0].hits ], pinnedPostIds );
					const isPinnedPresent = pinnedHits.some( hit => pinnedPostIds.includes( hit.objectID ) );

					if ( !isPinnedPresent ) {

						// Fetch the pinned record and insert it at the top
						fetchPinnedRecord( appId, searchApiKey, indexName, pinnedPostIds ).then( pinnedRecord => {

							if ( pinnedRecord ) {

								// If multiple pinned posts, pinnedRecord is an array
								if ( Array.isArray( pinnedRecord ) ) {

									// Remove any already present in pinnedHits
									const newPinned = pinnedRecord.filter(
										pr => !pinnedHits.some( hit => hit.objectID === pr.objectID )
									);
									pinnedHits.unshift( ...newPinned );

								} else {

									// Single pinned post
									if ( !pinnedHits.some( hit => hit.objectID === pinnedRecord.objectID ) ) {

										pinnedHits.unshift( pinnedRecord );

									}

								}

							} else {

								console.log( 'No pinned record fetched' );

							}
							renderWithHits( pinnedHits, pinnedPostIds );

						} );
						return; // Wait for async fetch

					}

					renderWithHits( pinnedHits, pinnedPostIds );

				} else if ( hasQuery ) {

					// Show no results state with the current search term
					showEmptyState( resultsContainer, currentRefinement );

				} else {

					resultsContainer.style.display = 'none';

				}

			}

		};

		// Create the custom widget
		const customAutocomplete = instantsearch.connectors.connectAutocomplete(
			renderAutocomplete
		);

		// Instantiate the custom widget
		search.addWidgets( [
			customAutocomplete( {
				container: '.js-searchBar',
				placeholder: 'Search for products',
				searchAsYouType: false,
			} )
		] );

		// Initialize the search phrase with the current URL parameter
		const urlParams = new URLSearchParams( window.location.search );
		const initialQuery = urlParams.get( 's' );
		if ( initialQuery ) {

			updateSearchPhrase( decodeURIComponent( initialQuery ) );

		}

	}

	if( document.querySelector( '.js-refinment-list' ) ) {

		/**
		 * Render pagination
		 * @param {Object} renderOptions - Render options
		 * @return {void}
		 */
		const renderPagination = ( renderOptions ) => {

			const {
				pages,
				currentRefinement,
				isFirstPage,
				isLastPage,
				refine,
				createURL,
			} = renderOptions;

			const container = document.querySelector( '#pagination' );

			// Check if query contains blocked terms
			const urlParams = new URLSearchParams( window.location.search );
			const currentQuery = urlParams.get( 's' ) || '';
			const isBlocked = containsBlockedTerm( currentQuery );

			// Hide pagination if query contains blocked terms
			if ( isBlocked && currentQuery ) {

				if ( container ) {

					container.innerHTML = '';
					container.style.display = 'none';

				}
				return;

			}

			if ( container ) {

				container.style.display = '';

				container.innerHTML = `
				<div class="ais-Pagination search-results__pagination">
					<ul class="ais-Pagination-list search-results__pagination__controls">
						${!isFirstPage ? `
							<li class="ais-Pagination-item ais-Pagination-item--previousPage search-results__pagination__controls__item">
								<a
									href="${createURL( currentRefinement - 1 )}"
									data-value="${currentRefinement - 1}"
									class="ais-Pagination-link search-results__pagination__controls__link"
									aria-label="Previous"
								>
									<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M6 11L1 6L6 1.00002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
									</svg>
								</a>
							</li>
						` : `
							<li class="ais-Pagination-item ais-Pagination-item--previousPage ais-Pagination-item--disabled search-results__pagination__controls__item">
								<span class="ais-Pagination-link search-results__pagination__controls__link search-results__pagination__controls__link--disabled" aria-label="Previous">
								<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M6 11L1 6L6 1.00002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
									</svg>
								</span>
							</li>
						`}

						${pages.map( page => `
							<li class="ais-Pagination-item ${currentRefinement === page ? 'ais-Pagination-item--selected search-results__pagination__controls__item--selected' : ''} search-results__pagination__controls__item">
								<a
									href="${createURL( page )}"
									data-value="${page}"
									class="ais-Pagination-link search-results__pagination__controls__link"
									aria-label="Page ${page + 1}"
									${currentRefinement === page ? 'aria-current="page"' : ''}
								>
									${page + 1}
								</a>
							</li>
						` ).join( '' )}

						${!isLastPage ? `
							<li class="ais-Pagination-item ais-Pagination-item--nextPage search-results__pagination__controls__item">
								<a
									href="${createURL( currentRefinement + 1 )}"
									data-value="${currentRefinement + 1}"
									class="ais-Pagination-link search-results__pagination__controls__link"
									aria-label="Next"
								>
									<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M6 11L1 6L6 1.00002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
									</svg>
								</a>
							</li>
						` : `
							<li class="ais-Pagination-item ais-Pagination-item--nextPage ais-Pagination-item--disabled search-results__pagination__controls__item">
								<span class="ais-Pagination-link search-results__pagination__controls__link search-results__pagination__controls__link--disabled" aria-label="Next">
									<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M6 11L1 6L6 1.00002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
									</svg>
								</span>
							</li>
						`}
					</ul>
				</div>
			`;

				[...container.querySelectorAll( 'a' )].forEach( element => {

					element.addEventListener( 'click', event => {

						event.preventDefault();
						refine( Number( event.currentTarget.dataset.value ) );

					} );

				} );

			}

		};

		/**
		 * Render pagination
		 * @param {Object} renderOptions - Render options
		 * @return {void}
		 */
		const customPagination = instantsearch.connectors.connectPagination(
			renderPagination
		);

		/**
		 * Render clear refinements
		 * @param {Object} renderOptions - Render options
		 * @param {boolean} isFirstRender - Whether this is the first render
		 * @return {void}
		 */
		const renderClearRefinements = ( renderOptions, isFirstRender ) => {

			const { canRefine, refine, widgetParams } = renderOptions;

			const container = document.querySelector( widgetParams.container );

			if ( isFirstRender ) {

				const button = document.createElement( 'button' );
				button.textContent = 'Clear Filters';
				button.classList.add( 'search-refinements__top__clear', 'clear-button' );

				button.addEventListener( 'click', () => {

					refine();

				} );

				container.appendChild( button );

			}

			container.querySelector( 'button' ).style.display = !canRefine ? 'none' : 'block';

		};

		/**
		 * Render clear refinements
		 * @param {Object} renderOptions - Render options
		 * @return {void}
		 */
		const customClearRefinements = instantsearch.connectors.connectClearRefinements(
			renderClearRefinements
		);

		/**
		 * Render hits
		 * @param {Object} renderOptions - Render options
		 * @return {void}
		 */
		const renderHits = ( renderOptions ) => {

			const { hits, widgetParams, results } = renderOptions;

			// Get current query from URL or results
			const urlParams = new URLSearchParams( window.location.search );
			const currentQuery = urlParams.get( 's' ) || ( results && results.query ) || '';
			const isBlocked = containsBlockedTerm( currentQuery );

			// Show blocked message if query contains blocked terms
			if ( isBlocked && currentQuery ) {

				const container = document.querySelector( widgetParams.container );
				if ( container ) {

					container.innerHTML = `
						<div class="${widgetParams.cssClasses.list.join( ' ' )} has-margin-bottom--xl">
							<div class="result-card result-card--blocked">
								<div class="result-card__title">
									<p>This search term is not allowed.</p>
								</div>
							</div>
						</div>
					`;
					return;

				}

			}

			// Pin the record to the top if present in search results
			let pinnedHits = pinRecordToTop( [ ...hits ], pinnedPostIds );
			const isPinnedPresent = pinnedHits.some( hit => pinnedPostIds.includes( hit.objectID ) );

			if ( !isPinnedPresent ) {

				// Fetch the pinned record and insert it at the top
				fetchPinnedRecord( appId, searchApiKey, indexName, pinnedPostIds ).then( pinnedRecord => {

					if ( pinnedRecord ) {

						// If multiple pinned posts, pinnedRecord is an array
						if ( Array.isArray( pinnedRecord ) ) {

							// Remove any already present in pinnedHits
							const newPinned = pinnedRecord.filter(
								pr => !pinnedHits.some( hit => hit.objectID === pr.objectID )
							);
							pinnedHits.unshift( ...newPinned );

						} else {

							// Single pinned post
							if ( !pinnedHits.some( hit => hit.objectID === pinnedRecord.objectID ) ) {

								pinnedHits.unshift( pinnedRecord );

							}

						}

						// Render with pinned records
						renderHitsWithPinned( pinnedHits, widgetParams );

					} else {

						// No pinned record found, render original hits
						renderHitsWithPinned( pinnedHits, widgetParams );

					}

				} );

			} else {

				// Pinned record already present, render with it at the top
				renderHitsWithPinned( pinnedHits, widgetParams );

			}

		};

		/**
		 * Render hits with pinned records
		 * @param {Array} hits - The hits to render
		 * @param {Object} widgetParams - Widget parameters
		 * @return {void}
		 */
		function renderHitsWithPinned( hits, widgetParams ) {

			const container = document.querySelector( widgetParams.container );

			container.innerHTML = `
				<div class="${widgetParams.cssClasses.list.join( ' ' )}">
					${hits.map( hit => `
						<div class="result-card ${pinnedPostIds.includes( hit.objectID ) ? 'result-card--pinned' : ''}">
							<div class="result-card__category">
								<p class="result-card__category-label">
									${hit.post_type.split( '_' )
		.map( word => word.charAt( 0 ).toUpperCase() + word.slice( 1 ) )
		.join( ' ' )}
								</p>
							</div>
							<div class="result-card__title">
								<p>${hit._highlightResult && hit._highlightResult.title ?
		instantsearch.highlight( {
			attribute: 'title',
			hit,
			highlightedTagName: 'mark'
		} ) :
		( hit.title || 'Untitled' )}</p>
							</div>
							<div class="result-card__content">
								<p>${hit._snippetResult && hit._snippetResult.content ?
		instantsearch.snippet( {
			attribute: 'content',
			hit,
			highlightedTagName: 'mark'
		} ) :
		truncateText( hit.content || '', 150 )}</p>
							</div>
							<a href="${hit.permalink}"
								class="result-card__permalink"
								title="${truncateText( hit.title, 50 )}">
							</a>
						</div>
					` ).join( '' )}
				</div>
			`;

		}

		/**
		 * Create the custom hits widget using the connector
		 * @param {Object} renderOptions - Render options
		 * @return {void}
		 */
		const customHits = instantsearch.connectors.connectHits( renderHits );

		search.addWidgets( [
			instantsearch.widgets.configure( {
				hitsPerPage: 15
			} ),
			instantsearch.widgets.refinementList( {
				container: "#refinement-list",
				attribute: "post_type",
				cssClasses: {
					list: ['refinement-filters__list'],
					item: ['refinement-filters__filter'],
					selectedItem: ['refinement-filters__filter--selected'],
				},
				/**
				 * Transform items
				 * @param {Array} items - Items to transform
				 * @return {Array} Transformed items
				 */
				transformItems( items ) {

					return items.map( item => ( {
						...item,
						highlighted: item.highlighted.toUpperCase(),
						label: item.label.includes( '_' ) ?
							item.label.split( '_' )
								.map( word => word.charAt( 0 ).toUpperCase() + word.slice( 1 ) )
								.join( ' ' ) :
							item.label.charAt( 0 ).toUpperCase() + item.label.slice( 1 )
					} ) );

				},
				templates: {

					/**
					 * Render item
					 * @param {Object} data - Item data object
					 * @param {string} data.label - Item label
					 * @param {number} data.count - Item count
					 * @param {boolean} data.isRefined - Whether item is refined
					 * @param {string} data.value - Item value
					 * @param {Object} options - Template options
					 * @param {Function} options.html - HTML template function
					 * @return {string} Rendered item
					 */
					item( { label, count, isRefined, value }, { html } ) {

						return html`
							<div class="field-control field-control--checkbox">
								<input type="checkbox" value="${value}" ${isRefined ? 'checked' : ''} />
								<label>
									<span class="field-control__label">${label}</span>
									<span class="field-control__count">(${count})</span>
								</label>
							</div>
						`;

					},
				},
			} ),
			customHits( {
				container: '#search-results',
				cssClasses: {
					list: ['search-results__list', 'list'],
				},
			} ),
			instantsearch.widgets.stats( {
				container: document.querySelector( '.js-search-refinements-found' ),
				templates: {
					/**
					 * Render stats
					 * @param {Object} data - Stats data object
					 * @param {Object} options - Template options
					 * @param {Function} options.html - HTML template function
					 * @return {string} Rendered stats
					 */
					text( data, { html } ) {

						// Check if query contains blocked terms
						const urlParams = new URLSearchParams( window.location.search );
						const currentQuery = urlParams.get( 's' ) || ( data.query ) || '';
						const isBlocked = containsBlockedTerm( currentQuery );

						if ( isBlocked && currentQuery ) {

							return html`<p>This search term is not allowed.</p>`;

						}

						let count = '';
						if ( data.hasManyResults ) {

							count += `${data.nbHits}`;

						} else if ( data.hasOneResult ) {

							count += '1';

						} else {

							count += '0';

						}
						return html`<p>Found: <span>${count}</span></p>`;

					},
				},
			} ),
			customClearRefinements( {
				container: '.js-search-refinements-clear',
			} ),
			customPagination( {
				container: document.querySelector( '#pagination' ),
				cssClasses: {
					root: 'search-results__pagination',
				},
			} )
		] );

	}

	search.start();

	/**
	 * Initialize sticky sidebar
	 * @return {void}
	 */
	const initStickySidebar = () => {

		const sidebar = document.querySelector( '.search-sidebar' );
		if ( !sidebar || window.innerWidth < 1024 ) {

			return;

		}

		// Add sticky class
		sidebar.classList.add( 'is-sticky' );

		let scrollTimeout;
		window.addEventListener( 'scroll', () => {

			if ( scrollTimeout ) {

				window.cancelAnimationFrame( scrollTimeout );

			}

		} );

		// let resizeTimeout;
		// window.addEventListener( 'resize', () => {
		//
		// 	if ( resizeTimeout ) {
		//
		// 		window.cancelAnimationFrame( resizeTimeout );
		//
		// 	}
		//
		// } );

	};

	initStickySidebar();

	// First check for required elements
	const searchContainer = document.querySelector( '.opt-search' );
	if ( !searchContainer ) {

		return;

	}

	const mainMenu = document.querySelector( '.b-nav' );

	/**
	 * Set search width
	 * @return {void}
	 */
	const setSearchWidth = () => {

		const searchBar = document.querySelector( '.js-searchBar' );
		if ( !mainMenu || !searchBar ) {

			return;

		}

		if ( window.matchMedia( '(min-width: 1280px)' ).matches ) {

			const searchIcon = document.querySelector( '.opt-search__icon' );
			if ( !searchIcon ) {

				return;

			}

			const mainMenuRect = mainMenu.getBoundingClientRect();
			const searchIconRect = searchIcon.getBoundingClientRect();

			searchBar.style.width = `${searchIconRect.right - mainMenuRect.left}px`;

		} else {

			searchBar.style.width = '';

		}

	};

	const mqDesktop = window.matchMedia( '(min-width: 1280px)' );

	// Run once on init
	setSearchWidth();

	// React ONLY to breakpoint changes
	mqDesktop.addEventListener( 'change', setSearchWidth );

	/**
	 * Show empty state UI
	 * @param {HTMLElement} container - Results container
	 * @param {string} [searchTerm] - Current search term
	 * @param {boolean} [isBlocked] - Whether the search term is blocked
	 */
	const showEmptyState = ( container, searchTerm = '', isBlocked = false ) => {

		if ( searchTerm ) {

			// Check if blocked term
			if ( isBlocked || containsBlockedTerm( searchTerm ) ) {

				// Blocked search term state
				container.innerHTML = `
					<div class="opt-search__result-group">
						<ul class="opt-search__results-list">
							<li class="opt-search__result-item">
								<div class="opt-search__empty-state">
									<p>This search term is not allowed.</p>
								</div>
							</li>
						</ul>
					</div>
				`;
				container.style.display = 'block';

			} else {

				// No results found state
				container.innerHTML = `
					<div class="opt-search__result-group">
						<ul class="opt-search__results-list">
							<li class="opt-search__result-item">
								<div class="opt-search__empty-state">
									<p>No results found for "${searchTerm}"</p>
								</div>
							</li>
						</ul>
					</div>
				`;
				container.style.display = 'block';

			}

		} else {

			// Just hide the container when there's no search term
			container.style.display = 'none';
			container.innerHTML = '';

		}

	};

}
